import { useCallback, useRef } from 'react';

/**
 * Keeps a second element sliding sideways in step with a scrolling one.
 *
 * For a header that has to stay put vertically while its columns still track a horizontal
 * scroller -- the therapist's week grid being the case this was written for. It cannot be
 * done in CSS alone: `position: sticky` is measured against the nearest scroll container,
 * so a header *inside* the sideways scroller has no vertical scrollport to stick to and
 * simply scrolls off the top of the page with everything else. Lifting it out of the
 * scroller gives it the page to stick to and takes its sideways movement away, which is
 * what this hands back.
 *
 * A transform rather than the mirror's own `scrollLeft`: it is not a scroll container, so
 * there is nothing to shift, and a transform is the cheap half of the pipeline anyway.
 *
 * Both are **callback refs**, for the same reason `useHorizontalOverflow` returns one --
 * the elements are conditionally rendered, so an object ref read once on mount would still
 * be null the render the week appears. They are stable, so composing this scroller ref with
 * that hook's on one element is safe:
 *
 *   const [scrollerRef, mirrorRef] = useMirroredScrollLeft<HTMLDivElement, HTMLDivElement>();
 */
export const useMirroredScrollLeft = <S extends HTMLElement, M extends HTMLElement>(): [
    (node: S | null) => void,
    (node: M | null) => void,
] => {
    const scroller = useRef<S | null>(null);
    const mirror = useRef<M | null>(null);

    const sync = useCallback((): void => {
        if (mirror.current) {
            mirror.current.style.transform = `translateX(${-(scroller.current?.scrollLeft ?? 0)}px)`;
        }
    }, []);

    const scrollerRef = useCallback(
        (node: S | null): void => {
            scroller.current?.removeEventListener('scroll', sync);
            scroller.current = node;
            node?.addEventListener('scroll', sync, { passive: true });

            // Whatever the scroller was already at: switching view or paging the week
            // remounts these, and a mirror left at zero over a grid scrolled halfway
            // across would put Thursday's name above Saturday's column.
            sync();
        },
        [sync]
    );

    const mirrorRef = useCallback(
        (node: M | null): void => {
            mirror.current = node;
            sync();
        },
        [sync]
    );

    return [scrollerRef, mirrorRef];
};
